/**
 * @module UpdateCustomerService
 * @remarks RTK Query service for updating customer data
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { UpdateCustomerCardRequest, UpdateCustomerCardResponse } from "@models/customer.model";
import { toast } from "keep-react";
import { t } from "i18next";
import { closeEditProfile } from "@store/features/userActionsSlice";

/**
 * API service for updating customer data
 */
export const updateCustomerCardApi = createApi({
  reducerPath: "updateCustomerCardApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Customer"],
  endpoints: (builder) => ({
    updateCustomerCard: builder.mutation<UpdateCustomerCardResponse[], UpdateCustomerCardRequest>({
      query: (request) => ({
        url: `data/kundenkarten/${request.card_id}`,
        method: "PATCH",
        body: {
          fields: {
            status: request.fields.status,
            kartentyp: 1
          }
        },
      }),
      // Transform response if needed
      transformResponse: (response: UpdateCustomerCardResponse[]) => {
        return response;
      },
      // Handle success and error with toast notifications
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          
          // Close the edit profile panel
          dispatch(closeEditProfile());
          toast.success(t("cards.requestCard.success"));
        } catch (err) {
          console.error(err);
          toast.error(t("cards.requestCard.error"));
        }
      },
      invalidatesTags: ["Customer"],
    }),
  }),
});

// Export hooks for use in components
export const { useUpdateCustomerCardMutation } = updateCustomerCardApi;